import axios from 'axios';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import useAxios from '../../hooks/useAxios';
import { VaultMediaResponseList } from '../../hooks/useSubscriberVaultMedias';
import { encodeBase64 } from '../../utils/common';
import { SettingsContext } from '../SettingsContext';

export type DownloadItem = {
    media: VaultMediaResponseList;
    subscriberId: string;
    username: string;
};

type DownloadContextType = {
    queue: DownloadItem[];
    current: DownloadItem | null;
    progress: number;
    totalBytes: number;
    loadedBytes: number;
    speed: number | null;
    etaSeconds: number | null;
    isDownloading: boolean;
    addToQueue: (item: DownloadItem) => void;
    cancelCurrent: () => void;
    removeFromQueue: (mediaId: number) => void;
    abortAll: () => void;
};

export const DownloadContext = createContext<DownloadContextType>({
    queue: [],
    current: null,
    progress: 0,
    totalBytes: 0,
    loadedBytes: 0,
    speed: null,
    etaSeconds: null,
    isDownloading: false,
    addToQueue: () => {},
    cancelCurrent: () => {},
    removeFromQueue: () => {},
    abortAll: () => {},
});

export const DownloadProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const hasStartedRef = useRef(false);

    const settingsContext = useContext(SettingsContext);
    const [queue, setQueue] = useState<DownloadItem[]>([]);
    const [current, setCurrent] = useState<DownloadItem | null>(null);
    const [progress, setProgress] = useState(0);
    const [totalBytes, setTotalBytes] = useState(0);
    const [loadedBytes, setLoadedBytes] = useState(0);
    const [downloadStartTime, setDownloadStartTime] = useState<number | null>(null);
    const [downloading, setDownloading] = useState(false);
    const axiosInstance = useAxios();
    const controllerRef = useRef<AbortController | null>(null);

    // Add to queue (prevent duplicates by media.id)
    const addToQueue = (item: DownloadItem) => {
        setQueue(prev => {
            const isDuplicate = prev.some(q => q.media.id === item.media.id) || current?.media.id === item.media.id;
            return isDuplicate ? prev : [...prev, item];
        });
    };

    // Remove a pending item by mediaId
    const removeFromQueue = (mediaId: number) => {
        setQueue(prev => prev.filter(item => item.media.id !== mediaId));
    };

    // Cancel the current download
    const cancelCurrent = () => {
        if (controllerRef.current) {
            controllerRef.current.abort();
        }
    };

    // Cancel the current download and clear all pending downloads.
    // The queue is cleared first so the download effect doesn't pick up the next item when the current one aborts.
    const abortAll = () => {
        setQueue([]);
        if (controllerRef.current) {
            controllerRef.current.abort();
        }
    };

    const elapsed = downloadStartTime ? (Date.now() - downloadStartTime) / 1000 : 0;
    const speed = elapsed > 0 ? loadedBytes / elapsed : null;
    const etaSeconds = speed && totalBytes > 0 ? (totalBytes - loadedBytes) / speed : null;

    useEffect(() => {
        if (downloading || current || queue.length === 0) return;

        setQueue(prevQueue => {
            const [next, ...rest] = prevQueue;
            if (next) {
                setCurrent(next);
                return rest;
            }
            return prevQueue;
        });
    }, [queue, downloading, current]);

    // Handle actual download logic
    useEffect(() => {
        if (!current || downloading || hasStartedRef.current) return;

        hasStartedRef.current = true; // prevent double start

        const controller = new AbortController();
        controllerRef.current = controller;

        const download = async () => {
            setDownloading(true);
            setProgress(0);
            setDownloadStartTime(Date.now());

            try {
                const fullUrl = current.media.full?.url;
                if (!fullUrl) throw new Error('Missing full media URL');

                const base64url = encodeBase64(fullUrl);
                const apiUrl = `${settingsContext.routes.onlyFans.base}${current.subscriberId}/vaultMediaFull/${
                    current.media.id
                }?url=${encodeURIComponent(base64url)}`;

                const response = await axiosInstance.get(apiUrl, {
                    responseType: 'blob',
                    signal: controller.signal,
                    timeout: 0,
                    onDownloadProgress: event => {
                        const { loaded, total } = event;
                        if (total) {
                            setProgress(Math.round((loaded * 100) / total));
                            setTotalBytes(total);
                        } else {
                            setProgress(-1);
                            setTotalBytes(0);
                        }
                        setLoadedBytes(loaded);
                    },
                });

                const blob = new Blob([response.data], { type: current.media.type });
                const filename = fullUrl.split('/').pop()?.split('?')[0] || `media-${current.media.id}`;
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                link.remove();
                URL.revokeObjectURL(link.href);
            } catch (err: any) {
                if (axios.isCancel(err) || err.name === 'AbortError') {
                    console.warn('Download cancelled');
                } else {
                    console.error('Download error', err);
                }
            } finally {
                setDownloading(false);
                setProgress(0);
                setCurrent(null);
                controllerRef.current = null;
                hasStartedRef.current = false; // reset so next file can start
            }
        };

        download();
    }, [current, downloading, axiosInstance, settingsContext.routes.onlyFans.base]);

    return (
        <DownloadContext.Provider
            value={{
                queue,
                current,
                progress,
                totalBytes,
                loadedBytes,
                speed,
                etaSeconds: Math.round(etaSeconds || 0),
                isDownloading: downloading,
                addToQueue,
                cancelCurrent,
                removeFromQueue,
                abortAll,
            }}
        >
            {children}
        </DownloadContext.Provider>
    );
};
